import axiosInstance from 'Utils/axiosInstance';
import companyAvatar from 'assets/company-avatar.svg';
import freelancerAvatar from 'assets/freelancer-avatar.svg';

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const IMAGE_BY_TYPE = {
    company: companyAvatar,
    freelancer: freelancerAvatar,
};

export async function getInvoicesCount() {
    const response = await axiosInstance.get('/invoices');

    if (!response?.data) return 0;

    return response.data.count;
}

export async function getInvoicesCountPendingValidation() {
    const params = new URLSearchParams({
        status: 'pending_validation',
    });
    const response = await axiosInstance.get('/invoices', { params });

    if (!response?.data) return 0;

    return response.data.count;
}

export async function getInvoicesCountDraft() {
    const params = new URLSearchParams({
        status: 'drafted',
    });
    const response = await axiosInstance.get('/invoices', { params });

    if (!response?.data) return 0;

    return response.data.count;
}

export async function getInvoicesCountWithErrors() {
    const params = new URLSearchParams({
        status: 'with_errors',
    });
    const response = await axiosInstance.get('/invoices', { params });

    if (!response?.data) return 0;

    return response.data.count;
}

function mapDtoToInvoice(dto) {
    return {
        ...dto,
        creation_date: new Date(dto.creation_date),
        date_emission: dto.date_emission ? new Date(dto.date_emission) : null,
        date_expiration: dto.date_expiration ? new Date(dto.date_expiration) : null,
        date_payment: dto.date_payment ? new Date(dto.date_payment) : null,
        date_operation: dto.date_operation ? new Date(dto.date_operation) : null,
        installments: Array.isArray(dto.installments)
            ? dto.installments
                  .map((installment) => ({
                      ...installment,
                      date: new Date(installment.date),
                  }))
                  .sort((a, b) => a.date - b.date)
            : [],
    };
}

function mapDtoToInvoiceParty(dto) {
    const image = dto.icon_url ? dto.icon_url : IMAGE_BY_TYPE[dto.type] || IMAGE_BY_TYPE.company;

    return {
        ...dto,
        image,
        creation_date: new Date(dto.creation_date),
    };
}

export async function getInvoicesPending() {
    const params = new URLSearchParams({
        is_validated: false,
    });
    const response = await axiosInstance.get('/invoices', { params });

    if (!response?.data) return [];

    return response.data.items.map(mapDtoToInvoice);
}

export async function getInvoices(searchParams) {
    const paramsNormalized = Object.keys(searchParams).reduce((acc, key) => {
        if (searchParams[key] !== null) {
            acc[key] = searchParams[key];
        }
        return acc;
    }, {});

    const params = new URLSearchParams(paramsNormalized);

    const response = await axiosInstance.get('/invoices', { params });

    if (!response?.data) return {};

    return {
        ...response.data,
        items: response.data.items.map(mapDtoToInvoice),
    };
}

export async function getInvoice(id) {
    const response = await axiosInstance.get(`/invoices/${id}`);

    if (!response?.data) return null;

    if (response.data.tax_authority_provider_status === 'error') {
        const errors = await getInvoiceErrors(id);
        if (Array.isArray(errors) && errors.length > 0) {
            response.data.issue_errors = errors;
        }
    }

    return mapDtoToInvoice(response.data);
}

export async function createContact(params) {
    const response = await axiosInstance.post('/contacts', params);

    if (!response?.data) return null;

    return mapDtoToInvoiceParty(response.data);
}

export async function updateContact(id, params) {
    const response = await axiosInstance.put(`/contacts/${id}`, params);

    if (!response?.data) return null;

    return mapDtoToInvoiceParty(response.data);
}

export async function getContact(id) {
    const response = await axiosInstance.get(`/contacts/${id}`);

    if (!response?.data) return null;

    return mapDtoToInvoiceParty(response.data);
}

export async function getContacts(searchParams = {}) {
    const paramsNormalized = Object.keys(searchParams).reduce((acc, key) => {
        if (searchParams[key] !== null) {
            acc[key] = searchParams[key];
        }
        return acc;
    }, {});

    const params = new URLSearchParams(paramsNormalized);

    const response = await axiosInstance.get('/contacts', { params });

    if (!response?.data) return {};

    return {
        ...response.data,
        items: response.data.items.map(mapDtoToInvoiceParty),
    };
}

export async function deleteContact(id) {
    await axiosInstance.delete(`/contacts/${id}`);
}

const STATIC_BULK_PARAMS = {
    tax_id: null,
};

export async function deleteContacts(body = {}) {
    await axiosInstance.put('/contacts', {
        action: 'delete',
        ...STATIC_BULK_PARAMS,
        value: null,
        ...body,
    });
}

export async function addContactsTaxonomiesTags(body = {}) {
    await axiosInstance.put('/contacts', {
        action: 'add_taxonomies_tags',
        ...STATIC_BULK_PARAMS,
        ...body,
    });
}

export async function removeContactsTaxonomiesTags(body = {}) {
    await axiosInstance.put('/contacts', {
        action: 'remove_taxonomies_tags',
        ...STATIC_BULK_PARAMS,
        ...body,
    });
}

export async function copyContactsToWorkspace(workspaceId) {
    await axiosInstance.post('/contacts/copy', {
        project_workspace_id: workspaceId,
    });
}

export async function exportContacts(params) {
    const paramsNormalized = Object.keys(params).reduce((acc, key) => {
        if (params[key] !== null && params[key] !== undefined) {
            acc[key] = params[key];
        }
        return acc;
    }, {});

    const response = await axiosInstance.post('/contacts/export', paramsNormalized);

    if (!response?.data) return null;

    return response.data;
}

export async function getExportContacts(exportId, exportFormat) {
    const response = await axiosInstance.get('/contacts/export/' + exportId, {
        params: { export_format: exportFormat },
    });

    if (!response?.data) return null;

    return response.data;
}

export async function deleteInvoice(id) {
    await axiosInstance.delete(`/invoices/${id}`);
}

export async function loadSendTemplate(invoiceId, templateId) {
    const response = await axiosInstance.post(`/invoices/${invoiceId}/send/preview`, { template_id: templateId });

    if (!response?.data) return null;

    return response.data;
}

export async function sendTemplate(invoiceId, email, title, body) {
    await axiosInstance.post(`/invoices/${invoiceId}/send`, { email, title, body });
}

export async function updateInvoice(id, params) {
    const response = await axiosInstance.put(`/invoices/${id}`, {
        is_validated: true,
        is_itemized: params.is_itemized || false,
        ...params,
    });

    if (!response?.data) return null;

    return mapDtoToInvoice(response.data);
}

export async function createInvoice(params) {
    const response = await axiosInstance.post('/invoices', {
        is_imported: true,
        ...params,
    });

    if (!response?.data) return null;

    return mapDtoToInvoice(response.data);
}

export async function attachInvoiceFile(id, file) {
    const normalizedFile = normalizeFileName(file);

    const params = {
        name: normalizedFile.name,
    };

    const urlResponse = await axiosInstance.post(`/invoices/${id}/attach`, params);

    if (!urlResponse?.data) {
        throw new Error(
            `Error getting upload url for invoice ${file.name} with size ${file.size} and type ${file.type}`,
        );
    }

    const { upload_url } = urlResponse.data;

    await fetch(upload_url, {
        method: 'PUT',
        body: normalizedFile,
    });
}

// "name.PDF" => "name.pdf"
function normalizeFileName(file) {
    const parts = file.name.split('.');
    const extension = parts.pop();
    const name = parts.join('.');
    const normalizeName = `${name}.${extension.toLowerCase()}`;

    const normalizedFile = new File([file], normalizeName, { type: file.type });
    return normalizedFile;
}

export async function uploadInvoice(file, { transactionId } = {}) {
    const normalizedFile = normalizeFileName(file);

    const params = {
        name: normalizedFile.name,
        ...(transactionId && { transaction_id: transactionId }),
    };

    const urlResponse = await axiosInstance.post(`/invoices/upload`, params);

    if (!urlResponse?.data) {
        throw new Error(
            `Error getting upload url for invoice ${file.name} with size ${file.size} and type ${file.type}`,
        );
    }

    const { upload_url, id } = urlResponse.data;

    await fetch(upload_url, {
        method: 'PUT',
        body: normalizedFile,
    });

    return {
        id,
    };
}

export async function deleteInvoices(invoicesIds) {
    const body = {
        invoices_ids: invoicesIds,
        operation: 'delete',
        value: null,
    };

    await axiosInstance.put('/invoices', body);
}

export async function getQuota() {
    const response = await axiosInstance.get('/invoices/quotas');

    if (!response?.data) return null;

    return {
        ...response.data,
        quota_restart_date: new Date(response.data.quota_restart_date),
    };
}

export async function exportInvoices(params) {
    const response = await axiosInstance.post('/invoices/export', params);

    if (!response?.data) return null;

    return response.data;
}

export async function getExportInvoices(exportId) {
    const response = await axiosInstance.get('/invoices/export/' + exportId);

    if (!response?.data) return null;

    return response.data;
}

export async function exportTaxes(params) {
    const response = await axiosInstance.post('/tax_estimate/export', params);

    if (!response?.data) return null;

    return response.data;
}

export async function getExportTaxes(exportId) {
    const response = await axiosInstance.get('/tax_estimate/export/' + exportId);

    if (!response?.data) return null;

    return response.data;
}

export async function getInvoicesStats(searchParams = {}) {
    const initialParams = {
        subtypes: ['standard', 'standard_corrective', 'simplified', 'simplified_corrective'],
    };
    const paramsNormalized = Object.keys(searchParams).reduce((acc, key) => {
        if (searchParams[key] !== null) {
            acc[key] = searchParams[key];
        }
        return acc;
    }, initialParams);

    const params = new URLSearchParams(paramsNormalized);
    const response = await axiosInstance.get('/invoices/stats', { params });

    if (!response?.data) return null;

    return response.data;
}

function mapDtoToInvoiceEmail(dto) {
    return {
        ...dto,
        date: new Date(dto.date),
        creation_date: new Date(dto.creation_date),
        last_update: new Date(dto.last_update),
        files: dto.files.map((file) => ({
            ...file,
            creation_date: new Date(file.creation_date),
            last_update: new Date(file.last_update),
        })),
    };
}
export async function getInvoicesEmails(searchParams = {}) {
    const paramsNormalized = Object.keys(searchParams).reduce((acc, key) => {
        if (searchParams[key] !== null) {
            acc[key] = searchParams[key];
        }
        return acc;
    }, {});

    const params = new URLSearchParams(paramsNormalized);
    const response = await axiosInstance.get('/emails', { params });

    if (!response?.data) return null;
    return {
        ...response.data,
        items: response.data.items.map(mapDtoToInvoiceEmail),
    };
}

export async function deleteInvoiceEmail(emailId) {
    await axiosInstance.delete(`/emails/${emailId}`);
}

export async function getEmailRender(emailId) {
    const response = await axiosInstance.get(`/emails/${emailId}/render`);

    if (!response?.data) return null;

    return response.data;
}

export async function getInvoiceEmailFileDownloadUrl(emailId, fileId) {
    const response = await axiosInstance.get(`/emails/${emailId}/files/${fileId}/download`);

    if (!response?.data) return null;

    return response.data.download_url;
}

export async function processInvoiceEmail(emailId) {
    const response = await axiosInstance.post(`/emails/${emailId}/process`);

    if (!response?.data) return null;

    return response.data;
}

async function getCSVImport(url) {
    const STATUS = {
        ERROR: 'ERROR',
        OK: 'OK',
    };

    const STATUS_ERROR_MAPPING = 'import_requires_mapping';

    const response = await axiosInstance.get(url);

    const fileId = url.split('/').pop();

    if (!response?.data) {
        throw new Error(`Error getting import status for file: ${fileId}`);
    }

    if (response.data.status === STATUS.OK) {
        return {
            requiresMapping: false,
            error: null,
            errorInfo: null,
            columns: null,
        };
    }

    if (response.data.status === STATUS.ERROR) {
        if (response.data.status_message.description === STATUS_ERROR_MAPPING) {
            return {
                requiresMapping: true,
                error: response.data.status_message.description,
                errorInfo: response.data.status_message.info,
                columns: response.data.status_message.info.columns,
            };
        }

        return {
            requiresMapping: false,
            error: response.data.status_message.description,
            errorInfo: response.data.status_message.info,
            columns: null,
        };
    }

    await wait(500);
    return await getCSVImport(url);
}

export async function importContacts(fileCSV, onProgress = () => {}) {
    const params = {
        name: fileCSV.name,
        size: fileCSV.size,
    };

    onProgress('uploading');

    const urlResponse = await axiosInstance.post('/contacts/import', params);

    if (!urlResponse?.data) {
        throw new Error(
            `Error getting upload url for file ${fileCSV.name} with size ${fileCSV.size} and type ${fileCSV.type}`,
        );
    }

    const { upload_url, id: fileId } = urlResponse.data;

    const uploadResponse = await fetch(upload_url, {
        method: 'PUT',
        body: fileCSV,
    });

    if (!uploadResponse.ok) {
        throw new Error(`Error uploading file ${fileCSV.name} with size ${fileCSV.size} and type ${fileCSV.type}`);
    }

    onProgress('processing');

    const url = `/contacts/import/${fileId}`;
    const importResponse = await getCSVImport(url);
    return {
        ...importResponse,
        importId: fileId,
    };
}

function mapDtoToContactsImport(dto) {
    return {
        ...dto,
        creation_date: dto.creation_date ? new Date(dto.creation_date) : null,
        contacts_deleted_date: dto.contacts_deleted_date ? new Date(dto.contacts_deleted_date) : null,
    };
}

export async function getContactsImports() {
    const response = await axiosInstance.get('/contacts/imports');

    if (!response?.data) return { count: 0, items: [] };

    return {
        count: response.data.length,
        items: response.data.map(mapDtoToContactsImport),
    };
}

export async function deleteContactsImportContacts(importId) {
    const response = await axiosInstance.delete(`/contacts/import/${importId}`);

    if (!response?.data) return null;

    return mapDtoToContactsImport(response.data);
}

export async function suggestContactsMapping(fileId) {
    const response = await axiosInstance.get(`/contacts/import/${fileId}/mapping-suggestion`);

    if (!response?.data) {
        throw new Error(`Error getting mapping suggestion for file ${fileId}`);
    }

    return response.data;
}

export async function mapContacts(fileId, mapping) {
    const response = await axiosInstance.post(`/contacts/import/${fileId}/map`, mapping);

    if (!response?.data) {
        throw new Error(`Error mapping file ${fileId}`);
    }

    const url = `/contacts/import/${fileId}`;
    const importResponse = await getCSVImport(url);

    return importResponse;
}

export async function getInvoicesCalendar(searchParams = {}) {
    const paramsNormalized = Object.keys(searchParams).reduce((acc, key) => {
        if (searchParams[key] !== null) {
            acc[key] = searchParams[key];
        }
        return acc;
    }, {});

    const params = new URLSearchParams(paramsNormalized);

    const response = await axiosInstance.get('/calendar', { params });

    if (!response?.data) return null;

    return response.data;
}

export async function calendarInspect(body) {
    const response = await axiosInstance.post('/calendar/inspect', body);

    if (!response?.data) return null;

    return response.data;
}

// import csv invoices
export async function importInvoices(fileCSV, onProgress = () => {}) {
    const params = {
        name: fileCSV.name,
        size: fileCSV.size,
    };

    onProgress('uploading');

    const urlResponse = await axiosInstance.post('/invoices/import', params);

    if (!urlResponse?.data) {
        throw new Error(
            `Error getting upload url for file ${fileCSV.name} with size ${fileCSV.size} and type ${fileCSV.type}`,
        );
    }

    const { upload_url, id: fileId } = urlResponse.data;

    const uploadResponse = await fetch(upload_url, {
        method: 'PUT',
        body: fileCSV,
    });

    if (!uploadResponse.ok) {
        throw new Error(`Error uploading file ${fileCSV.name} with size ${fileCSV.size} and type ${fileCSV.type}`);
    }

    onProgress('processing');

    const url = `/invoices/import/${fileId}`;
    const importResponse = await getCSVImport(url);
    return {
        ...importResponse,
        importId: fileId,
    };
}

export async function suggestInvoicesMapping(fileId) {
    const response = await axiosInstance.get(`/invoices/import/${fileId}/mapping-suggestion`);

    if (!response?.data) {
        throw new Error(`Error getting mapping suggestion for file ${fileId}`);
    }

    return response.data;
}

export async function mapInvoices(fileId, mapping) {
    const response = await axiosInstance.post(`/invoices/import/${fileId}/map`, mapping);

    if (!response?.data) {
        throw new Error(`Error mapping file ${fileId}`);
    }

    const url = `/invoices/import/${fileId}`;
    const importResponse = await getCSVImport(url);

    return importResponse;
}

function mapDtoToInvoicesImport(dto) {
    return {
        ...dto,
        creation_date: dto.creation_date ? new Date(dto.creation_date) : null,
        invoices_deleted_date: dto.invoices_deleted_date ? new Date(dto.invoices_deleted_date) : null,
    };
}

export async function getInvoicesImports() {
    const response = await axiosInstance.get('/invoices/import');

    if (!response?.data) return { count: 0, items: [] };

    return {
        count: response.data.count,
        items: response.data.items.map(mapDtoToInvoicesImport),
    };
}

export async function deleteInvoicesImportInvoices(importId) {
    const response = await axiosInstance.delete(`/invoices/import/${importId}`);

    if (!response?.data) return null;

    return mapDtoToInvoicesImport(response.data);
}

export async function dissociateRecurrence({ recurrenceId, invoiceId }) {
    const response = await axiosInstance.post(`/operations/${recurrenceId}/dissociate`, {
        invoice_id: invoiceId,
    });

    if (!response?.data) return null;

    return response.data;
}

export async function associateRecurrence({ recurrenceId, invoiceId, periodKey, periodicity }) {
    const response = await axiosInstance.post(`/operations/${recurrenceId}/associate`, {
        invoice_id: invoiceId,
        period_key: periodKey,
        periodicity,
    });

    if (!response?.data) return null;

    return response.data;
}

export async function updateInvoiceFields(invoiceId, fields) {
    const response = await axiosInstance.patch(`/invoices/${invoiceId}`, fields);

    if (!response?.data) {
        throw new Error(`Error updating invoice ${invoiceId}`);
    }

    return mapDtoToInvoice(response.data);
}

export async function getInvoiceTaxes() {
    const response = await axiosInstance.get('/taxes');

    if (!response?.data) return null;

    return response.data.filter((tax) => tax.is_active);
}

export async function getInvoiceUnitsMeasurement() {
    const response = await axiosInstance.get('/units-measurement');

    if (!response?.data) return null;

    return response.data;
}

export async function calculateInvoiceTotals(params) {
    const response = await axiosInstance.post('/invoices/calculate', params);

    if (!response?.data) {
        throw new Error('Error calculating invoice totals');
    }

    return response.data;
}

export async function getInvoiceSeries() {
    const response = await axiosInstance.get('/series');

    if (!response?.data) return [];

    return response.data.map((serie) => ({
        ...serie,
        last_generation_date: serie.last_generation_date ? new Date(serie.last_generation_date) : null,
    }));
}

export async function createInvoiceSerie(params) {
    const response = await axiosInstance.post('/series', params);

    if (!response?.data) {
        throw new Error('Error creating invoice serie');
    }

    return response.data;
}

export async function updateInvoiceSerie(id, params) {
    const response = await axiosInstance.put(`/series/${id}`, params);

    if (!response?.data) {
        throw new Error('Error updating invoice serie');
    }

    return response.data;
}

export async function deleteInvoiceSerie(id) {
    await axiosInstance.delete(`/series/${id}`);
}

export async function getInvoicePaymentMethods() {
    const response = await axiosInstance.get('/payment-methods');

    if (!response?.data) return [];

    return response.data;
}

export async function createInvoicePaymentMethod(data) {
    const response = await axiosInstance.post('/payment-methods', data);

    if (!response?.data) {
        throw new Error('Error creating invoice payment method');
    }

    return response.data;
}

export async function deleteInvoicePaymentMethod(id) {
    await axiosInstance.delete(`/payment-methods/${id}`);
}

export async function updateInvoicePaymentMethod(id, data) {
    const response = await axiosInstance.put(`/payment-methods/${id}`, data);

    if (!response?.data) {
        throw new Error('Error updating invoice payment method');
    }

    return response.data;
}

export async function getInvoicePreview(params) {
    const response = await axiosInstance.post('/invoices/preview', params);

    if (!response?.data) {
        return {
            data: null,
        };
    }

    return response.data;
}

export async function createInvoiceIssued(params) {
    const response = await axiosInstance.post('/invoices', {
        identifier: null,
        is_imported: false,
        is_itemized: true,
        ...params,
    });

    if (!response?.data) return null;

    return mapDtoToInvoice(response.data);
}

export async function updateInvoiceIssued(id, params) {
    const response = await axiosInstance.put(`/invoices/${id}`, {
        identifier: null,
        is_itemized: true,
        is_validated: true,
        ...params,
    });
    if (!response?.data) return null;

    return mapDtoToInvoice(response.data);
}

async function getInvoiceErrors(invoiceId) {
    const response = await axiosInstance.get(`/invoices/${invoiceId}/errors`);

    if (!response?.data) return [];

    return response.data.errors;
}

export async function validateInvoice(invoiceId, { validate_invoice_tax_authority = false } = {}) {
    const response = await axiosInstance.post(`/invoices/${invoiceId}/issue-validate`, {
        validate_invoice_tax_authority,
    });

    if (!response?.data) {
        throw new Error('Error issuing invoice');
    }
}

export async function issueInvoice(invoiceId) {
    try {
        await axiosInstance.post(`/invoices/${invoiceId}/issue-upload`);
    } catch (error) {
        if (Array.isArray(error?.response?.data?.errors) && error?.response?.data?.errors.length > 0) {
            return error.response.data.errors;
        }
        throw error;
    }
}

export async function getInvoiceLogo() {
    try {
        const response = await axiosInstance.get('/templates_invoices');

        if (!response?.data) return null;

        return response.data.view_url;
    } catch (error) {
        if (error.response?.status === 404) return null;

        throw error;
    }
}

export async function updateInvoiceLogo(file) {
    const normalizedFile = normalizeFileName(file);
    const params = {
        file_name: normalizedFile.name,
    };

    const response = await axiosInstance.post('/templates_invoices', params);

    if (!response?.data) {
        throw new Error(
            `Error getting upload url for invoice logo ${file.name} with size ${file.size} and type ${file.type}`,
        );
    }

    const { upload_url } = response.data;

    await fetch(upload_url, {
        method: 'PUT',
        body: normalizedFile,
    });
}

export async function deleteInvoiceLogo() {
    await axiosInstance.delete('/templates_invoices');
}

export async function getInvoiceSeriePreview(params) {
    const response = await axiosInstance.post('/series/preview', params);

    if (!response?.data) {
        throw new Error('Error generating invoice serie preview');
    }

    return response.data?.next_identifier ?? '';
}

export async function getInvoiceTaxAuthorityData(invoiceId) {
    try {
        const response = await axiosInstance.get(`/invoices/invoices-tax-authority/${invoiceId}`);

        if (!response?.data) return null;

        return {
            ...response.data,
        };
    } catch (error) {
        if (error.response?.status === 404) return {};

        throw error;
    }
}

export async function getInvoiceTaxAuthorityValues(invoiceId) {
    const response = await axiosInstance.post(`/invoices/invoices-tax-authority/${invoiceId}/values`);

    if (!response?.data) return null;

    return response.data;
}

export async function createInvoiceTaxAuthorityValues(params) {
    const response = await axiosInstance.post(`/invoices/invoices-tax-authority`, params);

    if (!response?.data) return null;

    return response.data;
}

export async function updateInvoiceTaxAuthorityValues(invoiceId, params) {
    const response = await axiosInstance.put(`/invoices/invoices-tax-authority/${invoiceId}`, params);

    if (!response?.data) return null;

    return response.data;
}

export async function markInvoiceAsNotDuplicate(id) {
    const response = await axiosInstance.patch(`/invoices/${id}`, { is_not_duplicated: true });

    if (!response?.data) return null;

    return mapDtoToInvoice(response.data);
}

export async function downloadInvoiceTaxAuthorityDocument(invoiceId, documentFormat) {
    const params = new URLSearchParams({
        document_format: documentFormat,
    });

    const response = await axiosInstance.get(`/invoices/invoices-tax-authority/${invoiceId}/download`, { params });

    if (!response?.data) return null;

    return response.data;
}

export async function getRelatedInvoices(invoiceId) {
    const response = await axiosInstance.get(`/invoices/${invoiceId}/related`);

    if (!response?.data) return [];

    return Object.fromEntries(
        Object.entries(response.data).map(([key, invoices]) => [key, invoices.map(mapDtoToInvoice)]),
    );
}

export async function scanInvoice(invoiceId) {
    await axiosInstance.post(`/invoices/${invoiceId}/scan`);
}

export async function cancelInvoice(invoiceId) {
    await axiosInstance.post(`/invoices/${invoiceId}/cancel`);
}

export async function getInvoiceReconciliationSuggestions(invoiceId, searchParams) {
    const paramsNormalized = Object.keys(searchParams).reduce((acc, key) => {
        if (searchParams[key] !== null) {
            acc[key] = searchParams[key];
        }
        return acc;
    }, {});

    const params = new URLSearchParams(paramsNormalized);

    const response = await axiosInstance.get(`/invoices/${invoiceId}/reconcile/suggestions`, { params });

    if (!response?.data) return {};

    return {
        ...response.data,
        items: response.data.items.map((item) => ({
            ...item,
            date_booked: new Date(item.date_booked),
            date_executed: new Date(item.date_executed),
        })),
    };
}

export async function reconcileInvoice(
    invoiceId,
    { transactionsIds, forceReconciliationStatus = null, forceTransactionsReconciliationStatus = null },
) {
    const body = {
        transactions_ids: transactionsIds,
        force_reconciliation_status: forceReconciliationStatus,
        force_transactions_reconciliation_status: forceTransactionsReconciliationStatus,
    };

    const response = await axiosInstance.post(`/invoices/${invoiceId}/reconcile`, body);

    if (!response?.data) return null;

    return mapDtoToInvoice(response.data);
}

export async function getTaxesEstimate({ dateFrom, dateTo, periodicity }) {
    const body = {
        date_from: dateFrom,
        date_to: dateTo,
        periodicity,
    };

    const response = await axiosInstance.post('/tax_estimate', body);

    if (!response?.data) return null;

    return response.data;
}

export async function getTaxesInspect({ type, dateFrom, dateTo, taxGroup, taxId, amount, amountTotal, page = 1 }) {
    const body = {
        type,
        date_from: dateFrom,
        date_to: dateTo,
        tax_group: taxGroup || null,
        tax_id: taxId || null,
        amount: amount || null,
        amount_total: amountTotal || null,
        page,
    };

    const response = await axiosInstance.post('/tax_estimate/inspect', body);

    if (!response?.data) return null;

    return response.data;
}

export async function convertInvoiceToDraft(id) {
    const response = await axiosInstance.post(`/invoices/${id}/to_draft`);

    if (!response?.data) return null;

    return mapDtoToInvoice(response.data);
}

export async function getContactByTaxId(taxId) {
    const params = new URLSearchParams({
        tax_id: taxId,
    });

    const response = await axiosInstance.get('/contacts', { params });

    if (!response?.data) return null;

    if (response.data.items.length === 0) return null;

    return mapDtoToInvoiceParty(response.data.items[0]);
}

export async function getInvoiceTemplate(id) {
    const params = new URLSearchParams({
        is_recurrent: true,
    });

    const response = await axiosInstance.get(`/invoices/${id}`, { params });

    if (!response?.data) return null;

    return mapDtoToInvoice(response.data);
}

export async function getInvoiceTaxAuthorityConfig() {
    const params = new URLSearchParams({
        contact_id_is_empty: true,
    });

    const response = await axiosInstance.get('/invoices/config-tax-authority', { params });

    if (!response?.data) return null;

    return response.data;
}

export async function createInvoiceTaxAuthorityConfig(params) {
    const response = await axiosInstance.post('/invoices/config-tax-authority', params);

    if (!response?.data) return null;

    return response.data;
}

export async function updateInvoiceTaxAuthorityConfig(configId, params) {
    const response = await axiosInstance.put(`/invoices/config-tax-authority/${configId}`, params);

    if (!response?.data) return null;

    return response.data;
}

export async function deleteInvoiceTaxAuthorityConfig(configId) {
    await axiosInstance.delete(`/invoices/config-tax-authority/${configId}`);
}

export async function getInvoiceTaxAuthorityConfigById(configId) {
    const response = await axiosInstance.get(`/invoices/config-tax-authority/${configId}`);

    if (!response?.data) return null;

    return response.data;
}

export async function getInvoiceTaxAuthorityConfigByContactId(contactId) {
    const params = new URLSearchParams({
        contact_id: contactId,
    });

    const response = await axiosInstance.get('/invoices/config-tax-authority', { params });

    if (!response?.data) return null;

    if (response.data.length === 0) return null;

    return response.data[0];
}

export async function autoIssueInvoices(invoicesIds) {
    const body = {
        invoices_ids: invoicesIds,
    };

    const response = await axiosInstance.post('/invoices/auto-issue', body);

    if (!response?.data) {
        throw new Error('Error auto-issuing invoices');
    }

    return response.data;
}
